// DEL-1 :: OWN DELIVERY SUPPORT
// Helpers shared by the cart, the quote action and the checkout payloads so the
// own delivery flow can be switched on without touching the Lalamove flow.

export const OWN_DELIVERY_MODE = "own";
export const DELIVERY_FULFILMENT_MODE_KEY = "delivery_fulfilment_mode";

export const OWN_DELIVERY_QUOTE_URL = "/own_delivery_quote";
export const LALAMOVE_QUOTE_URL = "/request/quotation";

const toLower = (value) =>
  value === undefined || value === null ? "" : String(value).trim().toLowerCase();

//STORE THE MODE COMING FROM THE BUSINESS LOCATION SO ACTIONS (WHICH HAVE NO PROPS) CAN READ IT
export const setDeliveryFulfilmentMode = (mode) => {
  try {
    const value = toLower(mode);
    if (!value) {
      localStorage.removeItem(DELIVERY_FULFILMENT_MODE_KEY);
      return;
    }
    localStorage.setItem(DELIVERY_FULFILMENT_MODE_KEY, value);
  } catch (err) {
    console.error("setDeliveryFulfilmentMode:", err);
  }
};

export const getDeliveryFulfilmentMode = () => {
  try {
    return toLower(localStorage.getItem(DELIVERY_FULFILMENT_MODE_KEY));
  } catch (err) {
    return "";
  }
};

export const isOwnDeliveryMode = (mode) => toLower(mode) === OWN_DELIVERY_MODE;

//TRUE ONLY WHEN THE BUSINESS IS CONFIGURED FOR OWN DELIVERY. EVERYTHING ELSE STAYS ON LALAMOVE
export const isOwnDelivery = () => isOwnDeliveryMode(getDeliveryFulfilmentMode());

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

//API MAY SEND true/false, 1/0 OR "true"/"false". UNDEFINED MEANS "NOT REPORTED" -> DO NOT BLOCK
const toBool = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "boolean") return value;
  const normalized = toLower(value);
  if (["1", "true", "yes", "y"].includes(normalized)) return true;
  if (["0", "false", "no", "n"].includes(normalized)) return false;
  return undefined;
};

const toInt = (value) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
};

const round2 = (value) => parseFloat((value ?? 0).toFixed(2));

// Per unit price including tax. `price` on a cart line is the line total for the whole
// quantity and already includes the modifiers, so it is the most accurate source.
const readUnitPriceIncTax = (item) => {
  const quantity = toNumber(item?.quantity);
  const linePrice = toNumber(item?.price);

  if (linePrice !== null && quantity !== null && quantity > 0) {
    return round2(linePrice / quantity);
  }

  return round2(toNumber(item?.unit_price) ?? toNumber(item?.product_price) ?? 0);
};

// Cart lines -> quote products. `product_variation_id` is the real variation, while
// `variation_id` on a cart line holds the joined modifier ids, so it is only the
// fallback for lines added straight from the menu (same rule the order payload uses).
export const buildOwnDeliveryQuoteProducts = (cartItems) => {
  if (!Array.isArray(cartItems)) return [];

  return cartItems
    .map((item) => ({
      product_id: toInt(item?.product_id),
      variation_id: toInt(item?.product_variation_id ?? item?.variation_id),
      quantity: toNumber(item?.quantity) ?? 1,
      unit_price_inc_tax: readUnitPriceIncTax(item),
      line_discount_type: "fixed",
      line_discount_amount: 0,
    }))
    .filter((product) => product.product_id !== null);
};

// Order level fields /own_delivery_quote expects. The app has no discount or coupon
// on this flow yet, so they stay at the documented defaults - wire them here the day
// the cart can carry them.
export const OWN_DELIVERY_QUOTE_DEFAULTS = {
  tax_rate_id: null,
  discount_type: "fixed",
  discount_amount: 0,
  service_charges: 0,
  coupon_amount_used: 0,
  coupon_type: null,
  small_order_fee: 0,
};

export const buildOwnDeliveryQuotePayload = (cartItems) => ({
  business_id: toInt(localStorage.getItem("business_id")),
  location_id: toInt(localStorage.getItem("location_id")),
  ...OWN_DELIVERY_QUOTE_DEFAULTS,
  products: buildOwnDeliveryQuoteProducts(cartItems),
});

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

//THE QUOTE FIELDS MAY ARRIVE AT THE ROOT OF THE RESPONSE OR UNDER data/own_delivery
const readQuote = (source, fallback = {}) => {
  const nested = source?.own_delivery ?? source?.quote ?? source?.data ?? {};
  return {
    deliveryCharges:
      toNumber(
        firstDefined(
          source?.delivery_charges,
          source?.delivery_charge,
          nested?.delivery_charges,
          nested?.delivery_charge
        )
      ) ?? fallback.deliveryCharges ?? 0,
    minOrderMet:
      toBool(firstDefined(source?.min_order_met, nested?.min_order_met)) ??
      fallback.minOrderMet,
    minOrderValue:
      toNumber(
        firstDefined(
          source?.min_order,
          source?.min_order_value,
          source?.minimum_order_value,
          nested?.min_order,
          nested?.min_order_value,
          nested?.minimum_order_value
        )
      ) ?? fallback.minOrderValue ?? null,
    //DEL-2 :: is_free OVERRIDES delivery_charges FOR DISPLAY - "Free delivery" INSTEAD OF THE FIGURE
    isFree:
      toBool(firstDefined(source?.is_free, source?.isFree, nested?.is_free, nested?.isFree)) ??
      fallback.isFree ??
      false,
    currency:
      firstDefined(source?.currency, source?.quoted_currency, nested?.currency) ??
      fallback.currency ??
      "SGD",
    message:
      firstDefined(
        source?.min_order_message,
        source?.message,
        nested?.min_order_message,
        nested?.message
      ) ?? fallback.message ?? "",
  };
};

//READS THE /own_delivery_quote RESPONSE BODY INTO THE SHAPE THE CART WORKS WITH
export const readOwnDeliveryQuote = (body) => readQuote(body);

// The quote is the source of truth for the delivery fee, EXCEPT when it comes back
// unconfirmed - no charge at all, or a bare 0 without is_free:true. That 0 doesn't mean
// "free", it means the quote didn't actually price the delivery, so charging nothing
// would undercharge the customer. In that case fall back to the store's own flat
// Own Delivery Fee (business_location.own_delivery_fee) instead of defaulting to $0.
export const resolveOwnDeliveryFee = (quote, configuredFee) => {
  if (quote?.isFree === true) return 0;

  const quotedFee = toNumber(quote?.deliveryCharges);
  if (quotedFee !== null && quotedFee > 0) return quotedFee;

  return toNumber(configuredFee) ?? 0;
};

// Own delivery has no third party quotation, so the outlet picker is fed from the
// business locations the app already loaded (/find_pickup_store). The list is kept in
// the same order as `state.products.outlates.business_location` because the outlet
// modal pairs the two by index.
export const buildOwnDeliveryOutlets = (businessLocations) => {
  if (!Array.isArray(businessLocations)) return [];

  return businessLocations.map((location) => ({
    ...location,
    //THE FEE IS QUOTED FROM THE CART AT CHECKOUT, NOT PER OUTLET. NO LALAMOVE `data.stops` PAYLOAD
    charges: { totalFee: 0, totalFeeCurrency: "SGD" },
  }));
};

export const isMinOrderMet = (quote) => quote?.minOrderMet !== false;

export const getMinOrderMessage = (quote, subTotal) => {
  if (quote?.message) return quote.message;

  const minOrderValue = quote?.minOrderValue;
  if (minOrderValue === null || minOrderValue === undefined) {
    return "Your order is below the minimum amount for delivery from this outlet. Please add a few more items to continue.";
  }

  const shortfall = parseFloat(minOrderValue) - (parseFloat(subTotal) || 0);
  const shortfallText =
    shortfall > 0 ? ` Please add $${shortfall.toFixed(2)} more to continue.` : "";

  return `This outlet delivers on orders of $${parseFloat(minOrderValue).toFixed(
    2
  )} and above.${shortfallText}`;
};

const readStoredDeliveryCharge = () => {
  try {
    const info = JSON.parse(localStorage.getItem("deliveryInformation"));
    return toNumber(info?.deliveryCharge) ?? 0;
  } catch (err) {
    return 0;
  }
};

const isDeliveryOrder = (data) =>
  data?.type_for_api === "Delivery" || Number(data?.order_type) === 1;

// Adds delivery_mode / delivery_charges to a /save_pickup_delivery payload.
// No-op for Lalamove businesses and for pickup orders.
export const applyOwnDeliveryToSavePayload = (data) => {
  if (!isOwnDelivery() || !data || typeof data !== "object" || Array.isArray(data)) {
    return data;
  }
  if (!isDeliveryOrder(data)) return data;

  //THE CALLERS DEFAULT delivery_charges TO 0, SO FALL BACK TO THE QUOTE WE STORED AT CHECKOUT
  const existingCharge = toNumber(data.delivery_charges);
  return {
    ...data,
    delivery_mode: data.delivery_mode || OWN_DELIVERY_MODE,
    delivery_charges: existingCharge > 0 ? existingCharge : readStoredDeliveryCharge(),
  };
};

// DEL-3 :: OWN DELIVERY TRACKING
// The store drives its own order status, so tracking is read from the order's
// `delivery_status_histories` instead of a Lalamove status string.

export const OWN_DELIVERY_TRACKING_STEPS = [
  { status: "received", label: "Order Received" },
  { status: "preparing", label: "Preparing" },
  { status: "out_for_delivery", label: "Out for Delivery" },
  { status: "delivered", label: "Delivered" },
];

const OUT_FOR_DELIVERY_INDEX = OWN_DELIVERY_TRACKING_STEPS.findIndex(
  (step) => step.status === "out_for_delivery"
);

//BOTH SPELLINGS - THE TERMINAL STATE IS NOT ONE OF THE FOUR STEPS
const CANCELLED_STATUSES = ["cancelled", "canceled"];

//"Out For Delivery" / "out-for-delivery" ALL COLLAPSE TO out_for_delivery
const normalizeTrackingStatus = (value) => toLower(value).replace(/[\s-]+/g, "_");

const readStatusHistories = (order) => {
  const histories = order?.delivery_status_histories;
  return Array.isArray(histories) ? histories.filter(Boolean) : [];
};

//OLDEST FIRST BY id. WITHOUT ids THE SORT IS STABLE, SO THE API'S OWN ORDER SURVIVES
const sortHistoriesOldestFirst = (histories) =>
  [...histories].sort((a, b) => (toNumber(a?.id) ?? 0) - (toNumber(b?.id) ?? 0));

// True for store-fulfilled orders. `delivery_mode` is the real signal; the history
// array is only a fallback for responses that omit the mode, and a Lalamove order
// never carries one.
export const isOwnDeliveryOrder = (order) => {
  if (isOwnDeliveryMode(order?.delivery_mode)) return true;
  return !order?.delivery_mode && readStatusHistories(order).length > 0;
};

//DRIVER DETAILS ARE STAMPED ON THE out_for_delivery ENTRY, BUT ANY ENTRY CARRYING THEM WILL DO
const readDriver = (orderedHistories) => {
  const withDriver = orderedHistories.filter(
    (entry) => entry?.driver_name || entry?.driver_phone
  );
  const outForDelivery = withDriver.filter(
    (entry) => normalizeTrackingStatus(entry?.to_status) === "out_for_delivery"
  );
  const source = outForDelivery[outForDelivery.length - 1] ?? withDriver[withDriver.length - 1];

  return {
    driverName: source?.driver_name ?? null,
    driverPhone: source?.driver_phone ?? null,
  };
};

// The backend redacts driver_phone to "•••• 6890" unless the transaction has
// share_driver_phone set (see DeliveryBroadcaster::maskPhone on the API side). A
// redacted number can't actually be dialled, so the UI must not offer a Call button
// that silently fails - this is the one signal the frontend has to detect that case.
export const isDialablePhone = (phone) => Boolean(phone) && !/[•*]/.test(String(phone));

// Current step + driver for the own delivery stepper.
//
// The transaction's own `delivery_status` / `driver_*` columns win when present:
// they are what the realtime event (DEL-4) writes onto the order, so they are
// never older than the history. Otherwise the latest history entry's `to_status`
// is used, and an order with no history yet has still been received, so step one
// stays lit.
export const readOwnDeliveryTracking = (order) => {
  const ordered = sortHistoriesOldestFirst(readStatusHistories(order));
  const latest = ordered[ordered.length - 1];

  // A flat status is only trusted when it is one this stepper can actually place.
  // Anything else (an unmapped column value, a placeholder) must NOT beat the
  // history, or the stepper freezes on step one while the history has moved on.
  const flatStatus = normalizeTrackingStatus(order?.delivery_status);
  const historyStatus = normalizeTrackingStatus(latest?.to_status);
  const isKnown = (value) =>
    Boolean(value) &&
    (CANCELLED_STATUSES.includes(value) ||
      OWN_DELIVERY_TRACKING_STEPS.some((step) => step.status === value));

  const status =
    (isKnown(flatStatus) ? flatStatus : "") || historyStatus || flatStatus || "received";
  const isCancelled = CANCELLED_STATUSES.includes(status);

  // Cancelling must NOT erase progress already made - the stepper keeps showing the
  // highest real step reached (received/preparing/out_for_delivery/delivered),
  // independent of the cancelled flag. "cancelled" itself never maps to a step, so
  // it's excluded here and surfaces only via `isCancelled`, which drives the banner.
  const stepIndexOf = (value) =>
    OWN_DELIVERY_TRACKING_STEPS.findIndex((step) => step.status === value);

  const highestHistoryIndex = ordered.reduce((max, entry) => {
    const idx = stepIndexOf(normalizeTrackingStatus(entry?.to_status));
    return idx > max ? idx : max;
  }, -1);
  const flatStepIndex = stepIndexOf(flatStatus);
  const highestKnownIndex = Math.max(highestHistoryIndex, flatStepIndex);

  //AN UNRECOGNISED STATUS AND NO HISTORY YET STILL KEEPS STEP ONE LIT
  const activeIndex = highestKnownIndex === -1 ? 0 : highestKnownIndex;

  const fromHistory = readDriver(ordered);
  const driverName = order?.driver_name ?? fromHistory.driverName;

  // The flat `driver_phone` column and the history entries can disagree on whether
  // the number is redacted (e.g. the column still holds an older masked broadcast
  // while a later history entry already carries the real number). Preferring the
  // flat column unconditionally means a full number already sitting in the SAME
  // API response stays hidden until something else (a refresh, a realtime event)
  // happens to refresh the flat column too. Instead, take whichever of the two is
  // actually dialable; only fall back to a masked value if neither is.
  const flatPhone = order?.driver_phone ?? null;
  const historyPhone = fromHistory.driverPhone;
  const driverPhone = isDialablePhone(flatPhone)
    ? flatPhone
    : isDialablePhone(historyPhone)
      ? historyPhone
      : flatPhone ?? historyPhone;

  return {
    status,
    isCancelled,
    activeIndex,
    driverName,
    driverPhone,
    //NOTHING TO SHOW WHEN THE DRIVER FIELDS ARE STILL null
    showDriver: activeIndex >= OUT_FOR_DELIVERY_INDEX && Boolean(driverName || driverPhone),
  };
};

// ---------------------------------------------------------------------------
// DEL-4 :: OWN DELIVERY REALTIME (PUSHER)
// Backend contract - App\Utils\DeliveryBroadcaster:
//   channel  private-delivery-order.{transaction_id}
//   event    delivery.status.updated
//   payload  version, event, transaction_id, order_token_no, business_id,
//            location_id, delivery_mode, delivery_status, driver_name,
//            driver_phone, updated_at
// ---------------------------------------------------------------------------

export const OWN_DELIVERY_EVENT = "delivery.status.updated";

//MATCHES DeliveryBroadcaster::PAYLOAD_VERSION - BUMP ONLY WHEN THE FIELDS BELOW CHANGE
export const OWN_DELIVERY_EVENT_VERSION = 1;

export const ownDeliveryChannelName = (transactionId) =>
  `private-delivery-order.${transactionId}`;

// The broadcaster keys the channel on the transaction id. `delivery_status_histories`
// entries carry that id explicitly, so they are the most reliable source when the
// order row itself exposes a different primary key.
export const readOwnDeliveryTransactionId = (order) => {
  const fromHistory = readStatusHistories(order)
    .map((entry) => entry?.transaction_id)
    .find((id) => id !== undefined && id !== null && id !== "");

  const id = firstDefined(order?.transaction_id, fromHistory, order?.id);
  return id === undefined ? null : String(id);
};

// Guards a realtime payload before it is allowed to touch order state:
//  - it must be for the order whose channel delivered it
//  - it must be a payload version this build knows how to read
//  - it must not be older than, or identical to, what was already applied
//
// NOTE: `version` is a schema version and is constant across events, so it can
// only gate compatibility - `updated_at` is what actually orders the events.
export const shouldApplyOwnDeliveryEvent = (data, expectedTransactionId, lastApplied) => {
  if (!data) return false;

  if (String(data.transaction_id) !== String(expectedTransactionId)) return false;

  const version = toNumber(data.version);
  if (version === null || version > OWN_DELIVERY_EVENT_VERSION) return false;

  if (!lastApplied) return true;

  const incomingAt = Date.parse(data.updated_at ?? "");
  const appliedAt = Date.parse(lastApplied.updated_at ?? "");

  if (!isNaN(incomingAt) && !isNaN(appliedAt)) {
    if (incomingAt < appliedAt) return false; //ARRIVED OUT OF ORDER
    if (incomingAt === appliedAt && lastApplied.delivery_status === data.delivery_status) {
      return false; //SAME EVENT DELIVERED TWICE
    }
    return true;
  }

  //WITHOUT USABLE TIMESTAMPS, ONLY A REAL STATUS CHANGE IS WORTH RE-RENDERING FOR
  return (
    lastApplied.delivery_status !== data.delivery_status ||
    lastApplied.driver_name !== data.driver_name ||
    lastApplied.driver_phone !== data.driver_phone
  );
};

// Merges a validated payload onto the order. Only the four fields the ticket
// calls for are written; everything else on the order is left as the API sent it.
export const applyOwnDeliveryEvent = (order, data) => ({
  ...order,
  delivery_mode: data?.delivery_mode || order?.delivery_mode,
  delivery_status: data?.delivery_status ?? order?.delivery_status,
  driver_name: data?.driver_name ?? order?.driver_name ?? null,
  driver_phone: data?.driver_phone ?? order?.driver_phone ?? null,
});
